import { userInvitationService } from '@/services/user-invitation.service';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface InvitationGuardProps {
  children: React.ReactNode;
}

export function InvitationGuard({ children }: InvitationGuardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Always start with checking=true if there's a token, to prevent flash
  const [isChecking, setIsChecking] = useState(!!token);
  const [isValid, setIsValid] = useState(!token); // Only valid immediately if no token
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Prevent duplicate checks
    if (hasChecked.current) return;

    const checkInvitation = async () => {
      if (!token) {
        setIsChecking(false);
        setIsValid(true); // No token = self-registration, allow through
        return;
      }

      hasChecked.current = true;

      try {
        const statusResponse = await userInvitationService.checkInvitationStatus(token);

        if (statusResponse.success && statusResponse.data) {
          const status = statusResponse.data.status;

          // Redirect to invalid-invitation page for cancelled/invalid
          if (status === 'cancelled') {
            setIsRedirecting(true);
            navigate('/invalid-invitation', { replace: true });
            return;
          }

          // Redirect to login for already registered
          if (status === 'registered') {
            setIsRedirecting(true);
            navigate('/login', { replace: true });
            return;
          }
        }

        // Token is valid, allow through
        setIsValid(true);
        setIsChecking(false);
      } catch (error: any) {
        // Check error code from backend
        const errorCode = error?.response?.data?.error?.error ||
                         error?.response?.data?.error ||
                         error?.response?.data?.code;

        if (errorCode === 'invitation_cancelled') {
          setIsRedirecting(true);
          navigate('/invalid-invitation', { replace: true });
          return;
        }

        if (errorCode === 'already_registered') {
          setIsRedirecting(true);
          navigate('/login', { replace: true });
          return;
        }

        // For other errors, let the Register page handle it
        setIsValid(true);
        setIsChecking(false);
      }
    };

    checkInvitation();
  }, [token, navigate]);

  // Show full-page loading while checking or redirecting
  if (isChecking || isRedirecting) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex flex-col items-center justify-center z-50">
        <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600">Verifying invitation...</p>
      </div>
    );
  }

  // Only render children if valid and not redirecting
  if (!isValid) {
    return null;
  }

  return <>{children}</>;
}

export default InvitationGuard;
